import { useCallback, useMemo, useRef } from 'react';
import {
  BodyScrollEvent,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  GridApi,
  ColumnState,
} from 'ag-grid-community';
import debounce from 'lodash/debounce';

import { adminColumnSharedFields } from '../../../shared/columnDefinitions';
import { LABEL_COLUMN_ID } from '../constants';
import { getDetailColumnWidth, useGridApiEventListener } from './utils';
import { accountColumnDefinition } from './getColumnDefinitions';

export const useSyncGridState = ({
  gridApi,
  parentGridApi,
}: {
  gridApi?: GridApi | null,
  parentGridApi: GridApi,
}) => {
  const parentScrollLock = useRef(false);
  const detailsScrollLock = useRef(false);

  const unlockDetailsScrollLock = useMemo(() => debounce(() => {
    detailsScrollLock.current = false;
  }, 50), []);

  const unlockParentScrollLock = useMemo(() => debounce(() => {
    parentScrollLock.current = false;
  }, 50), []);


  useGridApiEventListener(parentGridApi, 'columnResized', useCallback((event: ColumnResizedEvent | ColumnVisibleEvent) => {
    if (event.columns?.some(col => col.getColId() === LABEL_COLUMN_ID)) {
      gridApi?.setColumnWidths([{
        key: 'detailName',
        newWidth: getDetailColumnWidth({
          parentGridApi,
          gridApi,
        }),
      }]);
    }

    event.columns?.forEach(column => {
      gridApi?.applyColumnState({
        state: [
          {
            colId: column.getColId(),
            width: column.getActualWidth(),
          },
        ],
      });
    });
  }, [gridApi, parentGridApi]));

  useGridApiEventListener(parentGridApi, 'columnVisible', useCallback((event: ColumnVisibleEvent) => {
    event.columns?.forEach(column => {
      gridApi?.applyColumnState({
        state: [
          {
            colId: column.getColId(),
            hide: !column.isVisible(),
          },
        ],
      });
    });
  }, [gridApi]));

  useGridApiEventListener(parentGridApi, 'columnMoved', useCallback((event: ColumnMovedEvent) => {
    const stableColumns = new Set([adminColumnSharedFields.colId, 'account', 'detailName']);
    const stableColumState = gridApi?.getColumnState()
      .filter((col: ColumnState) => stableColumns.has(col.colId!)) || [];
    const parentBasedColumnState = event.api.getColumnState()
      .filter((col: ColumnState) => col.colId !== LABEL_COLUMN_ID);
    gridApi?.applyColumnState({
      state: [...stableColumState, ...parentBasedColumnState].map(({ colId }) => ({ colId })),
      applyOrder: true }
    );
  }, [gridApi]));

  useGridApiEventListener(parentGridApi, 'bodyScroll', useCallback((event: BodyScrollEvent) => {
    if (parentScrollLock.current) {
      return;
    }
    if (event.direction !== 'horizontal') {
      return;
    }
    detailsScrollLock.current = true;
    try {
      // @ts-ignore gridPanel is private, but we need to use it to set position
      gridApi.gridPanel.setHorizontalScrollPosition(event.left);
    } catch (error) {
      console.error('Internal Grid API changed, could not scroll parent grid'); // eslint-disable-line no-console
      console.error(error); // eslint-disable-line no-console
    }
    unlockDetailsScrollLock();
  }, [gridApi, unlockDetailsScrollLock]));

  useGridApiEventListener(gridApi, 'bodyScroll', useCallback((event: BodyScrollEvent) => {
    if (detailsScrollLock.current) {
      return;
    }
    if (event.direction !== 'horizontal') {
      return;
    }
    parentScrollLock.current = true;
    try {
      // @ts-ignore gridPanel is private, but we need to use it to position
      parentGridApi.gridPanel.setHorizontalScrollPosition(event.left);
    } catch (error) {
      console.error('Internal Grid API changed, could not scroll child grid'); // eslint-disable-line no-console
      console.error(error); // eslint-disable-line no-console
    }
    unlockParentScrollLock();
  }, [parentGridApi, unlockParentScrollLock]));

  const adjustColumnsToParent = useCallback(() => {
    setTimeout(() => {
      gridApi?.applyColumnState({
        state: [
          {
            colId: 'adminCol',
          },
          {
            colId: 'account',
            width: accountColumnDefinition.width,
          },
          {
            colId: 'detailName',
            width: getDetailColumnWidth({
              parentGridApi,
              gridApi,
            }),
          },
          ...parentGridApi.getColumnState(),
        ],
        applyOrder: true,
      });
    }, 0);
  }, [gridApi, parentGridApi]);


  useGridApiEventListener(gridApi, 'firstDataRendered', adjustColumnsToParent);
  useGridApiEventListener(parentGridApi, 'viewportChanged', adjustColumnsToParent);

  return {
    adjustColumnsToParent,
  };
};
